import ecommerceImg from "../assets/ecommercee.webp";
import { useNavigate } from 'react-router-dom';
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { useRef, useEffect, useState } from "react";
import buildingIcon from "../assets/building-icon.png";
import recommendationsGroup from "../assets/recommendations.png";
import visualSearchGroup from "../assets/upload-icon.png";
import chatbotGroup from "../assets/chatbot-icon.png";
import sliderGroup from "../assets/slider-icon.png";

const ScrollingWordsSection = () => {

  // const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        // setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle functions for dropdowns
  const [word, setWord] = useState('Today');

  useEffect(() => {
    const interval = setInterval(() => {
      setWord((prev) => (prev === 'Today' ? 'Tomorrow' : 'Today'));
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Helmet>
        <title>AI-Powered E-Commerce Platform | Personalized Shopping & Smart Solutions</title>
        <meta name="description" content="Transform your shopping experience with LENTERA’s AI-powered e-commerce platform. Enjoy personalized recommendations, visual search, AI chat support, dynamic pricing, smart inventory alerts and secure, seamless transactions." />
        <meta name="keywords" content="LENTERA e-commerce solutions, AI-driven shopping platform, real-time stock alerts, fraud detection in e-commerce, intelligent shopping assistant." />
      </Helmet>

      <section className="w-full bg-[#FFF6DA] py-20 px-4 sm:px-6">
        <div className="w-full max-w-[1312px] mx-auto flex flex-col lg:flex-row items-center justify-between gap-10">

          {/* LEFT CONTENT */}
          <motion.div
            className="w-full lg:w-[60%]"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <p className="text-[#212129] font-semibold text-sm mb-3 uppercase tracking-wider">
              E-Commerce
            </p>
            <h1 className="text-[#212129] text-[40px] md:text-[48px] leading-[52px] md:leading-[58px] font-extrabold mb-[24px]">
              Smarter Shopping <br className="hidden lg:block" />
              personalized for You.
            </h1>
            <p className="text-[#212129] text-[16px] md:text-[18px] leading-[28px] md:leading-[30px] max-w-[600px]">
              Experience an AI-powered e-commerce platform that understands your taste, predicts your needs and delivers faster.
            </p>
          </motion.div>

          {/* RIGHT IMAGE */}
          <motion.div
            className="w-full lg:w-[40%] flex justify-center lg:justify-end"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <img src={ecommerceImg} alt="Ecommerce Illustration" className="w-[380px] sm:w-[460px] md:w-[520px] lg:w-full max-w-[600px] h-auto object-contain" />
          </motion.div>
        </div>
      </section>

      {/* WHY AI SECTION */}
      <section className="bg-white py-20 px-4 sm:px-6 lg:px-12">
        <motion.div
          className="max-w-[1300px] mx-auto bg-[#F8F8F8] rounded-t-[40px] px-6 sm:px-10 py-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-block bg-[#E0E0E0] text-[#1A1A1A] text-sm font-medium px-4 py-1 rounded-full mb-4">
              AI IN E-COMMERCE
            </span>
            <h2 className="text-[32px] sm:text-[40px] md:text-[48px] font-extrabold text-[#1A1A1A] mb-4">
              Why AI with E-Commerce?
            </h2>
            <p className="text-[#4B4B4B] text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Our AI transforms your shopping journey with intelligent recommendations,
              faster search and a highly personalized experience.
            </p>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-end">
            {[...Array(4)].map((_, idx) => (
              <motion.div
                key={idx}
                className={`rounded-t-[40px] p-6 flex flex-col justify-between ${idx === 0 ? "bg-[#FEB9CE]" :
                  idx === 1 ? "bg-[#CBD5D4]" :
                    idx === 2 ? "bg-[#E5DCD2]" : "bg-[#FFC931]"
                  }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                viewport={{ once: true }}
              >
                <div>
                  <h3 className="font-bold text-lg text-[#1A1A1A] mb-2 leading-snug">
                    {idx === 0 && <>Personalized <br /> recommendations</>}
                    {idx === 1 && <>Visual search <br /> integration</>}
                    {idx === 2 && <>AI Chat <br /> assistant 24/7</>}
                    {idx === 3 && <>Dynamic Pricing <br /> Engine</>}
                  </h3>
                  <p className="text-sm text-[#3F3F3F]">
                    {idx === 0 && "Shop smarter with AI suggestions tailored to your style and shopping behavior."}
                    {idx === 1 && "Upload a photo — find matching products instantly with AI image recognition."}
                    {idx === 2 && "Get instant answers, product help and order support with our smart chatbot."}
                    {idx === 3 && "AI adjusts prices for the best deals based on trends, demand and your preferences."}
                  </p>
                </div>
                <img
                  src={
                    idx === 0 ? recommendationsGroup :
                      idx === 1 ? visualSearchGroup :
                        idx === 2 ? chatbotGroup :
                          sliderGroup
                  }
                  alt="Feature Illustration"
                  className="mt-6 w-full"
                />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section className="w-full">
        <motion.div
          className="bg-[#262626] px-4 md:px-12 py-14 lg:py-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row items-start justify-between gap-8">
            <div className="flex-1">
              <div className="inline-block bg-[#4A4A4A] text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                HOW IT WORKS
              </div>
              <h2 className="text-white text-[28px] sm:text-[30px] lg:text-[50px] font-bold leading-tight">
                AI That Learns With You
              </h2>
            </div>
            <div className="flex-2 text-white text-base sm:text-lg leading-relaxed pt-4 lg:pt-10">
              Our AI evolves with every interaction to deliver smarter, <br />
              more personalized shopping every time.
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3">
          {["#FFE05E", "#FFE0FB", "#A3DCFF"].map((color, i) => (
            <motion.div
              key={i}
              className={`p-10 sm:p-12 lg:p-16 flex flex-col justify-start gap-6`}
              style={{ backgroundColor: color }}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.2 }}
              viewport={{ once: true }}
            >
              <img src={buildingIcon} alt="Icon" className="w-[50px] h-auto" />
              <h3 className="text-xl sm:text-2xl font-semibold text-[#262626]">
                {i === 0 ? "Browse Freely" : i === 1 ? "Get Smarter Results" : "Enjoy Seamless Support"}
              </h3>
              <p className="text-[#262626] text-base sm:text-lg">
                {i === 0 ? "Our AI observes your likes, searches & clicks." : i === 1 ? "The more you shop, the better it gets." : "Real-time help, tracking and solutions via our smart assistant."}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* AI FEATURES SECTION */}
      <section className="bg-[#F9F9F9] py-20 px-4 sm:px-6 lg:px-12">
        <motion.div
          className="max-w-[1250px] mx-auto"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          {/* Top Section */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-6 mb-12">
            <div>
              <span className="inline-block bg-[#E0E0E0] text-[#1A1A1A] text-sm font-medium px-4 py-1 rounded-full mb-4">
                AI FEATURES
              </span>
              <h2 className="text-[30px] sm:text-[36px] md:text-[42px] lg:text-[48px] font-extrabold text-gray-900 leading-tight">
                Smart Features <br />
                Powered by AI
              </h2>
            </div>
            <p className="text-gray-700 text-base md:text-lg max-w-md">
              Experience intelligent tools that personalize, predict and enhance every step of your shopping journey.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="bg-white rounded-[36px] shadow-md p-6 sm:p-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Box */}
            <motion.div
              className="lg:col-span-4 flex flex-col justify-center"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                Additional features
              </h3>
              <p className="text-base text-gray-600">
                Built to deliver performance, reliability and an exceptional user experience at every touchpoint.
              </p>
            </motion.div>

            {/* Right Cards */}
            <div className="lg:col-span-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className={`rounded-2xl p-6 ${i === 0 ? "bg-[#E9DDFD]" : i === 1 ? "bg-[#DDF2DB]" : "bg-[#E8E8E8]"
                    }`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.2 }}
                  viewport={{ once: true }}
                >
                  <h4 className="text-md font-semibold text-gray-900 mb-2">
                    {i === 0
                      ? "Auto reorder suggestions"
                      : i === 1
                        ? "Fraud detection & Smart security"
                        : "Smart inventory & Restock alerts"}
                  </h4>
                  <p className="text-sm text-gray-800">
                    {i === 0
                      ? "Regular buyers get smart refill reminders for frequently purchased items — never run out again!"
                      : i === 1
                        ? "AI monitors unusual activity to ensure secure and seamless transactions."
                        : "Real-time stock tracking with intelligent alerts when your favorite products are back."}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default ScrollingWordsSection;